const baseURL = process.env.REACT_APP_API_URL;

const getTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
        return 'Asia/Jerusalem';
    }
};

/**
 * Stream a chat message to the admin AI chatbot via SSE.
 * Uses fetch + ReadableStream because EventSource doesn't support auth headers.
 *
 * @param {string} message - The user's question
 * @param {Array} history - Previous messages [{role, content}, ...]
 * @param {string} currentRoute - The current page route (e.g. "/app/clients/123")
 * @param {Object} callbacks - {onStatus, onToken, onComplete, onError}
 * @returns {AbortController} - Call .abort() to cancel the stream
 */
export const streamChat = (message, history, currentRoute, { onStatus, onToken, onComplete, onError }) => {
    const token = localStorage.getItem("auth");
    const controller = new AbortController();

    fetch(`${baseURL}/admin/chat/stream`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Timezone': getTimezone(),
        },
        body: JSON.stringify({ message, history, currentRoute }),
        signal: controller.signal,
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const read = () => {
                reader.read().then(({ done, value }) => {
                    if (done) return;

                    buffer += decoder.decode(value, { stream: true });

                    // Parse SSE events from buffer
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer

                    let currentEventType = null;

                    for (const line of lines) {
                        if (line.startsWith('event:')) {
                            currentEventType = line.substring(6).trim();
                        } else if (line.startsWith('data:')) {
                            const data = line.substring(5).trim();
                            if (!data) continue;

                            try {
                                const parsed = JSON.parse(data);
                                const eventType = currentEventType || parsed.type;

                                switch (eventType) {
                                    case 'status':
                                        onStatus?.(parsed.status);
                                        break;
                                    case 'token':
                                        onToken?.(parsed.text);
                                        break;
                                    case 'complete':
                                        onComplete?.(parsed.fullResponse);
                                        break;
                                    case 'error':
                                        onError?.(parsed.message || 'שגיאה לא ידועה');
                                        break;
                                    default:
                                        break;
                                }
                            } catch (e) {
                                // Skip unparseable lines
                            }

                            currentEventType = null;
                        }
                    }

                    read();
                }).catch(err => {
                    if (err.name !== 'AbortError') {
                        onError?.(err.message || 'שגיאת חיבור');
                    }
                });
            };

            read();
        })
        .catch(err => {
            if (err.name !== 'AbortError') {
                onError?.(err.message || 'שגיאת חיבור');
            }
        });

    return controller;
};
